import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Table,
  Spinner,
  Alert,
  Progress,
} from "reactstrap";
import classnames from "classnames";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  useGetPurchaseOrderByIdQuery,
  useUpdatePurchaseOrderByIdMutation,
  useUpdatePurchaseOrderItemByIDMutation,
  useBulkUpdatePurchaseOrderDeliveryTypeMutation,
  useUpdateStockAllocationTransferStatusMutation,
  useCreatePOFromUndeliverableMutation,
  useMarkUndeliverableMutation,
  useSplitUndeliverableMutation,
  useCancelPurchaseOrderMutation,
  useUpdatePurchaseOrderItemQuantityMutation,
  useGetPurchaseRequestDocumentsByPurchaseOrderQuery,
} from "slices/InventoryManagement/inventoryManagementSlice";
import { useGetSafetyAppApprovalByPermitIdQuery } from "slices/workflow/WorkFlowSlice";
import { useGetprojectByIdQuery, useGetProjectBudgetsByProjectIdQuery } from "slices/projects/projectSlice";
import ResourceApprovalOverviewTab from "pages/SafetyApp/WorkFlowApprovels/WorkFlowApprovelsOverview/ResourceApprovalOverviewTab";
import { allowClosePermit, allowRevokePermit } from "helpers/common_helper";
import { formatDateTime } from "helpers/date_utils";
import { ServiceContext } from "context/ServiceContextProvider";
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input } from "reactstrap";
import DeliveryComponent from "./DeliveryComponent";
import AuditLogTab from "./AuditLogTab";
import POEmailModal from "./POEmailModal";
import { Tooltip } from "react-tooltip";
import PriceViewComponent, { PriceViewCompact } from "../CommonComponents/PriceViewComponent";
import { IsValidPermissions } from "Routes/AuthProtected";
import ProductImage from "../CommonComponents/ProductImage";
import ProductThumbnail from "../CommonComponents/ProductThumbnail";
import { toast } from "react-toastify";
import SupplierSelectionModal from "../CommonComponents/SupplierSelectionModal";
import CancelReasonModal from "../../../Components/Common/CancelReasonModal";
import { getUserId } from "helpers/jwt-token-access/auth-token-header";

/** PO-mapped budget section: backend returns project_budget_allocations with these fields */
export interface ProjectBudgetAllocationItem {
  id: number | string;
  section_name: string;
  section_allocated_cost?: string | number;
  allocated_cost?: string | number;
  actual_cost?: string | number;
  mapped_cost?: string | number;
  section_remaining_cost?: string | number;
}

function normalizeProjectBudgetAllocations(
  raw: any[] | undefined
): ProjectBudgetAllocationItem[] {
  if (!raw?.length) return [];
  return raw.map((a: any) => ({
    id: a.id,
    section_name: a.section_name,
    section_allocated_cost: a.section_allocated_cost ?? a.allocated_cost,
    actual_cost: a.actual_cost,
    mapped_cost: a.mapped_cost,
    section_remaining_cost: a.section_remaining_cost,
  }));
}

// Purchase Order Product Item Component (Read-only, with optional undeliverable checkbox)
const PurchaseOrderProductItem = ({
  item,
  showUndeliverableCheckbox,
  isMarkUndeliverableSelected,
  onMarkUndeliverableToggle,
  onToggleDeliveryType,
  onUpdateStockTransferStatus,
  isUpdatingStockTransfer,
  onEditQuantity,
}: any) => {
  const getSupplierQuantityForItem = (rowItem: any) => {
    const mode = String(rowItem?.source_mode || "").toLowerCase();
    if (mode === "split") {
      return parseFloat(rowItem?.supplier_allocation_quantity || "0") || 0;
    }
    if (mode === "stock") {
      return 0;
    }
    const supplierQty = parseFloat(rowItem?.supplier_allocation_quantity || "0") || 0;
    if (supplierQty > 0) return supplierQty;
    return parseFloat(rowItem?.quantity || "0") || 0;
  };
  const isDelivered = item.delivery_status === "delivered";
  const isServiceCategory =
    (item.category_name || "").toUpperCase() === "SERVICES";
  const canMarkUndeliverable =
    showUndeliverableCheckbox &&
    isServiceCategory &&
    !isDelivered &&
    !item.item_undeliverable;
  const latestStockAllocation =
    Array.isArray(item?.stock_allocations) && item.stock_allocations.length > 0
      ? item.stock_allocations[0]
      : null;

  return (
    <tr className={item.item_undeliverable ? "table-danger" : ""}>
      {onToggleDeliveryType && (
        <td className="text-center align-middle">
          <div className="d-flex flex-column align-items-center">
            {!isDelivered && (
              <Input
                type="checkbox"
                className="form-check-input mb-1"
                checked={(item.delivery_type || "in_preparation") === "planned"}
                onChange={(e) =>
                  onToggleDeliveryType(
                    item.id,
                    e.target.checked ? "planned" : "in_preparation"
                  )
                }
              />
            )}

            {isDelivered && (
              <span className="badge bg-soft-success text-success mb-1">
                Delivered
              </span>
            )}

            {(item.delivery_type || "in_preparation") === "in_preparation" && (
              <span className="text-muted fs-11 text-center mt-1">
                This delivery is in preparation and not planned yet.
              </span>
            )}
            {latestStockAllocation && (
              <div className="mt-2 text-center">
                <Badge color="info" className="mb-1">
                  Transfer: {latestStockAllocation.transfer_status}
                </Badge>
                {latestStockAllocation.transfer_status === "cancelled" &&
                  String(latestStockAllocation.notes || "")
                    .toLowerCase()
                    .includes("po") &&
                  String(latestStockAllocation.notes || "")
                    .toLowerCase()
                    .includes("rejected") && (
                    <div>
                      <Badge color="warning" className="mb-1">
                        Released due to PO rejection
                      </Badge>
                    </div>
                  )}
                <div className="d-flex gap-1 justify-content-center flex-wrap">
                  {latestStockAllocation.transfer_status === "allocated" && (
                    <Button
                      size="sm"
                      color="outline-primary"
                      disabled={isUpdatingStockTransfer}
                      onClick={() =>
                        onUpdateStockTransferStatus?.(
                          latestStockAllocation.id,
                          "issued"
                        )
                      }
                    >
                      Issue
                    </Button>
                  )}
                  {latestStockAllocation.transfer_status === "issued" && (
                    <Button
                      size="sm"
                      color="outline-warning"
                      disabled={isUpdatingStockTransfer}
                      onClick={() =>
                        onUpdateStockTransferStatus?.(
                          latestStockAllocation.id,
                          "in_transit"
                        )
                      }
                    >
                      In Transit
                    </Button>
                  )}
                  {(latestStockAllocation.transfer_status === "in_transit" ||
                    latestStockAllocation.transfer_status ===
                      "partial_received") && (
                    <Button
                      size="sm"
                      color="outline-success"
                      disabled={isUpdatingStockTransfer}
                      onClick={() =>
                        onUpdateStockTransferStatus?.(
                          latestStockAllocation.id,
                          "received",
                          latestStockAllocation.issued_qty ||
                            latestStockAllocation.allocated_from_stock_qty
                        )
                      }
                    >
                      Receive
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </td>
      )}
      <td>
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0 avatar-md bg-light rounded p-1">
            <ProductThumbnail
              attachment_urls={item?.attachment_urls}
              alt={item?.product_name || ''}
              size="lg"
              className="me-2"
            />
            {/* {item.thumbnail_url ? (
              <img
                src={item.thumbnail_url}
                alt={item.product_name}
                className="img-fluid d-block rounded"
                style={{ width: "60px", height: "60px", objectFit: "cover" }}
              />
            ) : null}
            {!item.thumbnail_url && (
              <div className="avatar-title bg-soft-primary text-primary rounded w-100 h-100 d-flex align-items-center justify-content-center">
                <i className="ri-box-3-line fs-18"></i>
              </div>
            )} */}
          </div>
          <div className="flex-grow-1 ms-3">
            <h6 className="fs-14 mb-1">
              <span className="text-dark">{item.product_name}</span>
              {item.is_rfq && (
                <Badge color="warning" className="ms-2 fs-10">
                  RFQ
                </Badge>
              )}
            </h6>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              {item.category_name && (
                <Badge color="primary" className="badge-border">
                  <i className="ri-price-tag-3-line align-middle me-1"></i>
                  {item.category_name}
                </Badge>
              )}
              {item.measurement_name && (
                <Badge color="primary" className="badge-border">
                  <i className="ri-ruler-line align-middle me-1"></i>
                  {item.measurement_name}
                </Badge>
              )}
            </div>
            {item.tag_names && item.tag_names.length > 0 && (
              <div className="mt-1">
                <small className="text">
                  Tags: {item.tag_names.join(", ")}
                </small>
              </div>
            )}
            <div className="mt-1">
              <small className="text-muted">
                Product ID: {item.product} • Item ID: {item.id}
              </small>
            </div>
          </div>
        </div>
      </td>
      <td>
        <div className="text-center">
          <span className="fw-bold fs-16 text-dark">
            {["split", "stock"].includes(String(item?.source_mode || "").toLowerCase())
              ? getSupplierQuantityForItem(item)
              : item.quantity}
          </span>
          {["split", "stock"].includes(String(item?.source_mode || "").toLowerCase()) && (
            <div className="text-muted fs-11 mt-1">
              Supplier: {getSupplierQuantityForItem(item)} | Stock:{" "}
              {item.stock_allocation_quantity || 0}
            </div>
          )}
          {item.measurement_name && (
            <div className="text-muted fs-12">{item.measurement_name}</div>
          )}
          {onEditQuantity && !isDelivered && (
            <Button
              size="sm"
              color="link"
              className="p-0 mt-1 text-decoration-none"
              onClick={() => onEditQuantity(item)}
            >
              <i className="ri-edit-line align-middle me-1"></i>
              Edit Qty
            </Button>
          )}
        </div>
      </td>
      <td>
        <div className="text-center">
          {item.expected_delivery_date ? (
            <div>
              <span className="badge bg-soft-success text-success fw-medium">
                <i className="ri-calendar-line align-middle me-1"></i>
                {new Date(item.expected_delivery_date).toLocaleDateString()}
              </span>
              <div className="text-muted fs-12 mt-1">
                {new Date(item.expected_delivery_date).toLocaleDateString(
                  "en-US",
                  {
                    weekday: "short",
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  }
                )}
              </div>
            </div>
          ) : (
            <span className="text-muted fst-italic">Not specified</span>
          )}
        </div>
      </td>
      <td>
        <div className="max-w-200">
          {item.delivery_note ? (
            <div>
              <span className="text-dark fw-medium">Note:</span>
              <p className="text-muted mb-0 fs-13">{item.delivery_note}</p>
            </div>
          ) : (
            <span className="text-muted fst-italic">No notes</span>
          )}
        </div>
      </td>
      <td>
        <div className="text-center">
          {item.supplier_name ? (
            <div className="text-start">
              <span className="fw-medium text-dark">{item.supplier_name}</span>
              {item.supplier_contact && (
                <div className="text-muted fs-12">{item.supplier_contact}</div>
              )}
              {item.supplier_email && (
                <div className="text-muted fs-12">{item.supplier_email}</div>
              )}
              {!!item?.stock_allocations?.length && (
                <div className="mt-1">
                  {item.stock_allocations.map((alloc: any) => (
                    <div key={alloc.id} className="text-muted fs-11">
                      Stock Ref: {alloc.source_stock_reference || alloc.allocation_no || `Alloc ${alloc.id}`} | Qty {alloc.allocated_from_stock_qty}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : item.is_rfq ? (
            <Badge color="warning" className="fs-12">
              RFQ - Supplier Required
            </Badge>
          ) : (
            <span className="text-muted fst-italic">No supplier</span>
          )}
        </div>
      </td>
      <td>
        <div className="text-center">
          {(item.effective_total_item_cost != null ? item.effective_total_item_cost : item.total_item_cost) ? (
            <div>
              {item.quantity && (
                <div className="text-muted fs-12">
                  <PriceViewComponent
                    price={item.effective_total_item_cost ?? item.total_item_cost}
                    quantity={Math.max(getSupplierQuantityForItem(item), 1)}
                    unitPrice={item.effective_unit_price}
                    showPerUnit={true}
                  />
                </div>
              )}
            </div>
          ) : (
            <span className="text-muted fst-italic">Not set</span>
          )}
        </div>
      </td>
      {canMarkUndeliverable && (
        <td className="text-center align-middle">
          {item.item_undeliverable ? (
            <Badge color="danger">
              <i className="ri-close-circle-line align-middle me-1"></i>
              Undeliverable
            </Badge>
          ) : isDelivered ? (
            <span className="text-muted">—</span>
          ) : (
            <Input
              type="checkbox"
              checked={isMarkUndeliverableSelected || false}
              onChange={(e) =>
                onMarkUndeliverableToggle?.(item.id, e.target.checked)
              }
              disabled={item.item_undeliverable}
            />
          )}
        </td>
      )}
    </tr>
  );
};

const InventoryPurchaseOrderDetails = () => {
  const { purchaseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const context = useContext(ServiceContext);
  const { selectedRoleName, getEmployeeUUID } = context;

  const [confirmCloseModal, setConfirmCloseModal] = useState(false);
  const [confirmRevokeModal, setConfirmRevokeModal] = useState(false);
  const [confirmCancelModal, setConfirmCancelModal] = useState(false);
  const [approvalData, setApprovalData] = useState<any>([]);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState("1");

  const {
    data: documentChecklistData,
    isLoading: isDocumentChecklistLoading,
    error: documentChecklistError,
  } = useGetPurchaseRequestDocumentsByPurchaseOrderQuery(
    {
      purchase_order_number: purchaseId as string,
    },
    {
      skip: !purchaseId,
    }
  );

  // Add these states to your existing useState declarations
  const [confirmCreateDeliveryModal, setConfirmCreateDeliveryModal] = useState(false);
  const [isCreatingDelivery, setIsCreatingDelivery] = useState(false);

  // PO cut-off: create new PO from undeliverable items
  const [showCreatePOFromUndeliverableModal, setShowCreatePOFromUndeliverableModal] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const [newPONotes, setNewPONotes] = useState("");
  // Single amount to allocate from remaining balance
  const [splitAmount, setSplitAmount] = useState<string>("");

  // Supplier selection modal for undeliverable split
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [selectedSupplierDetails, setSelectedSupplierDetails] = useState<any>(null);

  // Special case: also update supplier cost mapping from split amounts
  const [updateCostMapping, setUpdateCostMapping] = useState<boolean>(false);

  // Multi-supplier split UI state (supplier + per-item amounts)
  const [splitGroups, setSplitGroups] = useState<
    Array<{
      supplier: any | null;
      notes: string;
      itemAmounts: Record<number, string>;
    }>
  >([]);
  const [activeGroupIndex, setActiveGroupIndex] = useState<number | null>(null);

  // PO cut-off: mark undeliverable (Order Items tab)
  const [orderItemsMarkUndeliverableIds, setOrderItemsMarkUndeliverableIds] = useState<number[]>([]);
  const [orderItemsUndeliverableNotes, setOrderItemsUndeliverableNotes] = useState("");

  // Delivery planning selection (Order Items tab)
  const [isMarkingOrderItemsPlanned, setIsMarkingOrderItemsPlanned] = useState(false);

  // Helper: compute balance quantity/cost for undeliverable items
  const computeUndeliverableBalance = (item: any) => {
    const qty = parseFloat(item.quantity) || 0;
    const delivered = parseFloat(item.delivered_quantity) || 0;
    const totalCost = parseFloat(item.total_item_cost) || 0;
    const balanceQty = Math.max(0, qty - delivered);
    const unitPrice = qty > 0 ? totalCost / qty : 0;
    const balanceCost = balanceQty * unitPrice;
    return { balanceQty, balanceCost };
  };

  // Add this function to toggle the create delivery modal
  const toggleConfirmCreateDeliveryModal = () =>
    setConfirmCreateDeliveryModal(!confirmCreateDeliveryModal);

  // Add this mutation hook after your existing mutation hooks
  const [updatePurchaseOrder] = useUpdatePurchaseOrderByIdMutation();
  const [createPOFromUndeliverable, { isLoading: isCreatingPOFromUndeliverable }] =
    useCreatePOFromUndeliverableMutation();
  const [splitUndeliverable, { isLoading: isSplittingUndeliverable }] =
    useSplitUndeliverableMutation();
  const [markUndeliverable, { isLoading: isMarkingUndeliverable }] =
    useMarkUndeliverableMutation();
  const [cancelPurchaseOrder, { isLoading: isCancellingPO }] =
    useCancelPurchaseOrderMutation();
  const [cancelPOModalOpen, setCancelPOModalOpen] = useState(false);
  const [showCancellationEmailModal, setShowCancellationEmailModal] =
    useState(false);
  const [cancelPOError, setCancelPOError] = useState<string | null>(null);
  const [cancelPOErrorItems, setCancelPOErrorItems] = useState<string[]>([]);
  const [cancelPOResult, setCancelPOResult] = useState<any | null>(null);

  // --- PO Update flow: edit item quantity ---
  const [updatePOItemQuantity, { isLoading: isUpdatingQuantity }] =
    useUpdatePurchaseOrderItemQuantityMutation();
  const [editQtyModalOpen, setEditQtyModalOpen] = useState(false);
  const [editQtyItem, setEditQtyItem] = useState<any | null>(null);
  const [editQtyValue, setEditQtyValue] = useState<string>("");
  const [editQtyNeedsReapproval, setEditQtyNeedsReapproval] = useState(false);
  const [editQtyReason, setEditQtyReason] = useState("");
  const [editQtyError, setEditQtyError] = useState<string | null>(null);
  // Feature 1: where the added units go when the PO already has a delivery
  const [editQtyDeliveryAction, setEditQtyDeliveryAction] = useState<
    "merge" | "new_record"
  >("merge");
  const [editQtyDeliveryDate, setEditQtyDeliveryDate] = useState("");

  const openEditQuantityModal = (item: any) => {
    setEditQtyItem(item);
    setEditQtyValue(String(item?.quantity ?? item?.ordered_quantity ?? ""));
    setEditQtyNeedsReapproval(false);
    setEditQtyReason("");
    setEditQtyError(null);
    setEditQtyDeliveryAction("merge");
    setEditQtyDeliveryDate("");
    setEditQtyModalOpen(true);
  };

  const closeEditQuantityModal = () => {
    if (isUpdatingQuantity) return;
    setEditQtyModalOpen(false);
    setEditQtyItem(null);
  };

  const handleSubmitEditQuantity = async () => {
    if (!purchaseOrder?.id || !editQtyItem) return;
    const parsed = Number(editQtyValue);
    if (Number.isNaN(parsed) || parsed < 0) {
      setEditQtyError("Please enter a valid non-negative quantity.");
      return;
    }
    const currentQty = Number(
      editQtyItem?.quantity ?? editQtyItem?.ordered_quantity ?? 0
    );
    if (parsed === currentQty) {
      setEditQtyError("New quantity is the same as the current quantity.");
      return;
    }
    const isIncrease = parsed > currentQty;
    const showDeliveryGrouping =
      isIncrease && !!purchaseOrder?.is_delivery_created;
    if (
      showDeliveryGrouping &&
      editQtyDeliveryAction === "new_record" &&
      !editQtyDeliveryDate
    ) {
      setEditQtyError(
        "Please choose a delivery date for the new delivery record."
      );
      return;
    }
    setEditQtyError(null);
    try {
      const res: any = await updatePOItemQuantity({
        id: purchaseOrder.id,
        item_id: editQtyItem.id,
        new_quantity: parsed,
        needs_reapproval: editQtyNeedsReapproval,
        reason: editQtyReason,
        ...(showDeliveryGrouping
          ? {
              delivery_action: editQtyDeliveryAction,
              ...(editQtyDeliveryAction === "new_record"
                ? { new_delivery_date: editQtyDeliveryDate }
                : {}),
            }
          : {}),
      }).unwrap();
      if (res?.over_budget) {
        toast.warning(
          res?.budget_warning ||
            "This update exceeds the allocated budget for one or more sections."
        );
      }
      if (res?.reapproval_started) {
        toast.info("Purchase order sent for re-approval.");
      }
      toast.success(res?.message || "Quantity updated successfully.");
      setEditQtyModalOpen(false);
      setEditQtyItem(null);
      await refetchPurchaseOrder();
      // Refresh the approval workflow data so the Approval tab reflects the new total.
      try {
        await refetchProject();
      } catch {
        /* non-fatal */
      }
    } catch (err: any) {
      const msg =
        err?.data?.error ||
        err?.data?.detail ||
        "Failed to update the item quantity.";
      setEditQtyError(msg);
      toast.error(msg);
    }
  };

  const handleCancelPO = async (reason: string) => {
    if (!purchaseOrder?.id) return;
    setCancelPOError(null);
    setCancelPOErrorItems([]);
    try {
      const res: any = await cancelPurchaseOrder({
        id: purchaseOrder.id,
        reason,
        cancelled_by_user_id: getUserId(),
      });
      if (res?.error) {
        const data = res.error?.data || {};
        const drs = data.active_delivery_requests || [];
        setCancelPOError(
          drs.length
            ? "Cannot cancel this purchase order. Cancel the following delivery request(s) first:"
            : data.error || "Failed to cancel purchase order.",
        );
        setCancelPOErrorItems(
          drs.map((d: any) =>
            d.type === "split"
              ? `Split #${d.batch_number} — ${d.product_name || "Item"} (${d.delivery_status})`
              : `${d.product_name || "Item"} (${d.delivery_status})`,
          ),
        );
        return;
      }
      toast.success(res?.data?.message || "Purchase order cancelled successfully.");
      setCancelPOModalOpen(false);
      setCancelPOResult(res?.data || null);
      // Refresh the PO so status badges, the Cancel button and tab guards update
      refetchPurchaseOrder();
    } catch (e) {
      setCancelPOError("Failed to cancel purchase order.");
    }
  };
  const [updatePurchaseOrderItem] = useUpdatePurchaseOrderItemByIDMutation();
  const [bulkUpdatePurchaseOrderDeliveryType] =
    useBulkUpdatePurchaseOrderDeliveryTypeMutation();
  const [updateStockAllocationTransferStatus, { isLoading: isUpdatingStockTransfer }] =
    useUpdateStockAllocationTransferStatusMutation();

  // Handle create PO from undeliverable
  const handleCreatePOFromUndeliverable = async () => {
    if (!selectedSupplierId) {
      toast.error("Please select a supplier");
      return;
    }
    // Items selection is optional; if none, API will use all undeliverable items
    try {
      const remaining =
        purchaseOrder?.undeliverable_summary?.remaining_balance ?? 0;
      const amount = parseFloat(splitAmount || "0");

      if (!amount || amount <= 0) {
        toast.error("Please enter a valid amount to allocate");
        return;
      }
      if (amount > remaining) {
        toast.error(
          `Amount cannot be more than remaining balance (${remaining.toFixed(
            2,
          )}).`,
        );
        return;
      }

      const result = await createPOFromUndeliverable({
        poId: purchaseOrder?.id,
        body: {
          new_supplier_id: selectedSupplierId,
          item_ids: selectedItemIds.length > 0 ? selectedItemIds : undefined,
          split_amount: amount,
          notes: newPONotes || undefined,
          update_cost_mapping: updateCostMapping || undefined,
        },
      }).unwrap();
      toast.success("New PO created successfully from undeliverable items");
      setShowCreatePOFromUndeliverableModal(false);
      setSelectedSupplierId(null);
      setSelectedItemIds([]);
      setNewPONotes("");
      setSplitAmount("");
      refetchPurchaseOrder();
      // Navigate to new PO if ID is returned
      if (result?.id) {
        navigate(`/purchase-orders/${result.id}`);
      }
    } catch (error: any) {
      console.error("Error creating PO from undeliverable:", error);
      toast.error(error?.data?.error || "Failed to create new PO");
    }
  };

  const handleCreateDelivery = async () => {
    try {
      setIsCreatingDelivery(true);
      await updatePurchaseOrder({
        id: purchaseOrder?.id,
        values: {
          is_delivery_created: true,
        },
      }).unwrap();

      await refetchPurchaseOrder();
      toggleConfirmCreateDeliveryModal();
      setActiveTab("4"); // Switch to delivery tab
    } catch (error) {
      console.error("Error creating delivery:", error);
    } finally {
      setIsCreatingDelivery(false);
    }
  };

  const {
    data: purchaseOrder,
    isLoading,
    error,
    refetch: refetchPurchaseOrder,
  } = useGetPurchaseOrderByIdQuery(purchaseId);

  // Robust cancelled flag (covers po_status plus the cancellation audit fields)
  const isPOCancelled =
    purchaseOrder?.po_status === "cancelled" || !!purchaseOrder?.cancelled_at;

  // Project id from PO (for fallback project budget fetch when API doesn't return project_budget_allocations)
  const projectId =
    purchaseOrder?.project_id != null
      ? String(purchaseOrder.project_id)
      : purchaseOrder?.project != null
        ? typeof purchaseOrder.project === "object"
          ? String((purchaseOrder.project as any)?.id ?? "")
          : String(purchaseOrder.project)
        : undefined;

  const { data: projectData } = useGetprojectByIdQuery(projectId!, { skip: !projectId });
  const { data: projectBudgetsData } = useGetProjectBudgetsByProjectIdQuery(
    { projectId: projectId!, page: 1, pageSize: 100 },
    { skip: !projectId }
  );

  // Show only the PO-mapped budget section: from API (project_budget_allocations with section_name, section_allocated_cost, actual_cost, mapped_cost) or from project_budget_id chosen at PO creation
  const projectBudgetAllocations = useMemo(() => {
    if (purchaseOrder?.project_budget_allocations?.length > 0) {
      return normalizeProjectBudgetAllocations(purchaseOrder.project_budget_allocations);
    }
    const poBudgetId =
      purchaseOrder?.project_budget_id ??
      purchaseOrder?.purchase_request?.project_budget_id ??
      (typeof purchaseOrder?.project_budget === "object" ? (purchaseOrder?.project_budget as any)?.id : purchaseOrder?.project_budget) ??
      purchaseOrder?.budget_section_id;
    const poBudgetIds = purchaseOrder?.project_budget_ids ?? (poBudgetId != null ? [poBudgetId] : []);
    const sections = projectBudgetsData?.results ?? [];
    if (poBudgetIds.length === 0) return [];
    const sectionIds = new Set(poBudgetIds.map((id: any) => Number(id)));
    return sections
      .filter((s: any) => sectionIds.has(Number(s.id)))
      .map((s: any) => ({
        id: s.id,
        section_name: s.section_name,
        section_allocated_cost: s.allocated_cost,
        actual_cost: s.actual_cost ?? 0,
        mapped_cost: 0,
        section_remaining_cost: (Number(s.allocated_cost) || 0) - (Number(s.actual_cost) || 0),
      }));
  }, [purchaseOrder?.project_budget_allocations, purchaseOrder?.project_budget_id, purchaseOrder?.project_budget_ids, purchaseOrder?.purchase_request?.project_budget_id, purchaseOrder?.project_budget, purchaseOrder?.budget_section_id, projectBudgetsData?.results]);

  const projectAllocatedBudget =
    purchaseOrder?.project_allocated_budget ??
    projectData?.project_allocated_budget ??
    approvalData?.project_allocated_budget ??
    (approvalData?.project as any)?.project_allocated_budget;

  // Get undeliverable items (after purchaseOrder is declared)
  const undeliverableItems = purchaseOrder?.items?.filter((item: any) => item.item_undeliverable) || [];
  const activeSplitItemForSupplierModal = useMemo(() => {
    if (activeGroupIndex == null || !splitGroups[activeGroupIndex]) return null;
    const group = splitGroups[activeGroupIndex];
    const selectedEntry = Object.entries(group.itemAmounts || {}).find(
      ([, value]) => (parseFloat(String(value || "0")) || 0) > 0,
    );
    if (!selectedEntry) return null;
    const selectedItemId = Number(selectedEntry[0]);
    return (
      undeliverableItems.find(
        (item: any) => Number(item.id) === Number(selectedItemId),
      ) || null
    );
  }, [activeGroupIndex, splitGroups, undeliverableItems]);

  // Handle mark undeliverable from Order Items tab
  const handleOrderItemsMarkUndeliverable = async () => {
    if (orderItemsMarkUndeliverableIds.length === 0) {
      toast.error("Please select at least one item to mark as undeliverable");
      return;
    }
    if (!orderItemsUndeliverableNotes.trim()) {
      toast.error("Please provide a reason for marking items as undeliverable");
      return;
    }
    const poId = purchaseOrder?.id;
    if (!poId) return;
    try {
      await markUndeliverable({
        po_id: poId,
        item_ids: orderItemsMarkUndeliverableIds,
        undeliverable_notes: orderItemsUndeliverableNotes,
      }).unwrap();
      toast.success("Items marked as undeliverable");
      setOrderItemsMarkUndeliverableIds([]);
      setOrderItemsUndeliverableNotes("");
      refetchPurchaseOrder();
    } catch (error: any) {
      console.error("Error marking undeliverable:", error);
      toast.error(error?.data?.error || "Failed to mark items as undeliverable");
    }
  };

  const handleOrderItemsMarkUndeliverableToggle = (itemId: number, checked: boolean) => {
    setOrderItemsMarkUndeliverableIds((prev) =>
      checked ? [...prev, itemId] : prev.filter((id) => id !== itemId)
    );
  };

  const handleOrderItemsDeliveryTypeToggle = async (
    itemId: number,
    newType: "in_preparation" | "planned"
  ) => {
    const item = purchaseOrder?.items?.find((it: any) => it.id === itemId);
    if (!item) return;
    try {
      setIsMarkingOrderItemsPlanned(true);
      const formData = new FormData();
      formData.append("item_id", itemId.toString());
      formData.append(
        "pr_id",
        (item.purchase_request || item.purchase_request).toString()
      );
      formData.append("delivery_type", newType);
      await updatePurchaseOrderItem({
        id: itemId,
        values: formData,
      }).unwrap();
      refetchPurchaseOrder();
    } catch (error: any) {
      console.error("Failed to update delivery_type from Order Items tab", error);
      toast.error(error?.data?.error || "Failed to update item planning status");
    } finally {
      setIsMarkingOrderItemsPlanned(false);
    }
  };

  const handleUpdateStockTransferStatus = async (
    allocationId: number,
    transferStatus:
      | "issued"
      | "in_transit"
      | "received"
      | "partial_received"
      | "cancelled",
    quantity?: string | number
  ) => {
    try {
      await updateStockAllocationTransferStatus({
        id: allocationId,
        values: {
          transfer_status: transferStatus,
          ...(quantity !== undefined ? { quantity } : {}),
        },
      }).unwrap();
      toast.success("Stock transfer status updated");
      refetchPurchaseOrder();
    } catch (error: any) {
      console.error("Failed to update stock transfer status", error);
      toast.error(error?.data?.error || "Failed to update stock transfer status");
    }
  };

  const orderItemsPlanningTargets = useMemo<
    Array<{ id: number; planned: boolean; purchaseRequest: any }>
  >(() => {
    return (purchaseOrder?.items || [])
      .filter((item: any) => String(item.delivery_status || "").toLowerCase() !== "delivered")
      .map((item: any) => ({
        id: item.id as number,
        planned: (item.delivery_type || "in_preparation") === "planned",
        purchaseRequest: item.purchase_request,
      }));
  }, [purchaseOrder?.items]);

  const allOrderItemsPlanningChecked =
    orderItemsPlanningTargets.length > 0 &&
    orderItemsPlanningTargets.every((item) => item.planned);

  const handleOrderItemsBulkPlanningToggle = async (checked: boolean) => {
    if (!orderItemsPlanningTargets.length) return;
    try {
      setIsMarkingOrderItemsPlanned(true);
      const nextType = checked ? "planned" : "in_preparation";
      await bulkUpdatePurchaseOrderDeliveryType({
        delivery_type: nextType,
        item_ids: orderItemsPlanningTargets.map((item) => item.id),
        split_ids: [],
      }).unwrap();
      refetchPurchaseOrder();
    } catch (error: any) {
      console.error("Failed to bulk update order item planning", error);
      toast.error(error?.data?.error || "Failed to update planning for all items");
    } finally {
      setIsMarkingOrderItemsPlanned(false);
    }
  };

  const {
    data: workFlowData,
    isLoading: workFlowLoading,
    refetch: refetchProject,
  } = useGetSafetyAppApprovalByPermitIdQuery(purchaseId);

  const toggleConfirmCloseModal = () =>
    setConfirmCloseModal(!confirmCloseModal);
  const toggleConfirmRevokeModal = () =>
    setConfirmRevokeModal(!confirmRevokeModal);
  const toggleConfirmCancelModal = () =>
    setConfirmCancelModal(!confirmCancelModal);
  const toggleTab = (tab: any) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  // Add this helper function after your existing helper functions
  const isPOApprovedForDelivery = () => {
    return (
      purchaseOrder?.po_status === "approved" &&
      purchaseOrder?.approval_status_name === "approved"
    );
  };

  useEffect(() => {
    if (workFlowData) {
      setApprovalData(workFlowData);
    }
  }, [workFlowData]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const approval = queryParams.get("approval");
    if (approval === "true") {
      toggleTab("2");
    }
  }, [location]);

  // Define PO status options for badge display
  const getPOStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { color: string; label: string } } = {
      draft: { color: "secondary", label: "Draft" },
      submitted: { color: "warning", label: "Submitted" },
      approved: { color: "success", label: "Approved" },
      rejected: { color: "danger", label: "Rejected" },
      resubmit: { color: "info", label: "Resubmit" },
      ordered: { color: "info", label: "Ordered" },
      delivered: { color: "primary", label: "Delivered" },
      pending: { color: "warning", label: "Pending" },
      cancelled: { color: "danger", label: "Cancelled" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      label: status,
    };
    return <Badge color={config.color}>{config.label}</Badge>;
  };

  // Define delivery status options for badge display
  const getDeliveryStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { color: string; label: string } } = {
      pending: { color: "warning", label: "Pending" },
      partial: { color: "info", label: "Partial" },
      delivered: { color: "success", label: "Delivered" },
      cancelled: { color: "danger", label: "Cancelled" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      label: status,
    };
    return <Badge color={config.color}>{config.label}</Badge>;
  };

  // Calculate total cost (use effective_total_item_cost for POs created from undeliverable split)
  const calculateTotalCost = () => {
    if (!purchaseOrder?.items) return 0;
    return purchaseOrder.items.reduce((total: number, item: any) => {
      const cost = item.effective_total_item_cost ?? item.total_item_cost;
      return total + (parseFloat(cost) || 0);
    }, 0);
  };

  // Helper function to check if PO is in approval state
  const isPOInApprovalState = () => {
    const approval = (purchaseOrder?.approval_status_name || "").toString().toLowerCase();
    const status = (purchaseOrder?.po_status || "").toString().toLowerCase();

    return (
      status === "submitted" ||
      approval.startsWith("pending") ||
      approval === "in_review"
    );
  };

  const canEditNotes = () => {
    const approval = (purchaseOrder?.approval_status_name || "").toString().toLowerCase();
    const status = (purchaseOrder?.po_status || "").toString().toLowerCase();

    return status === "submitted" && approval.startsWith("pending");
  };

  // Helper function to check if PO is approved
  const isPOApproved = () => {
    return (
      purchaseOrder?.po_status === "approved" ||
      purchaseOrder?.approval_status_name === "approved"
    );
  };

  // Helper function to check if PO is in draft state
  const isPODraft = () => {
    return (
      purchaseOrder?.po_status === "draft" ||
      purchaseOrder?.approval_status_name === "draft"
    );
  };

  const [editNotesModal, setEditNotesModal] = useState<{
    open: boolean;
    notes: string[];
  }>({
    open: false,
    notes: [""],
  });

  const openEditNotesModal = () => {
    const raw = purchaseOrder?.notes;

    let initial: string[] = [""];

    if (Array.isArray(raw)) {
      initial = raw.map((v: any) => String(v));
    } else if (typeof raw === "string") {
      initial = [raw];
    } else if (raw && typeof raw === "object") {
      initial = Object.values(raw).map((v: any) => String(v));
    }

    if (!initial.length) {
      initial = [""];
    }

    setEditNotesModal({
      open: true,
      notes: initial,
    });
  };

  const closeEditNotesModal = () => {
    setEditNotesModal({
      open: false,
      notes: [""],
    });
  };

  const handleSaveNotes = async () => {
    if (!purchaseOrder?.id) return;

    const normalizedNotes = editNotesModal.notes
      .map((n) => n.trim())
      .filter((n) => n.length > 0);

    if (normalizedNotes.length === 0) {
      toast.error("Notes cannot be empty.");
      return;
    }

    try {
      await updatePurchaseOrder({
        id: purchaseOrder.id,
        values: { notes: normalizedNotes },
      }).unwrap();
      toast.success("PO notes updated successfully.");
      closeEditNotesModal();
      await refetchPurchaseOrder();
    } catch (error: any) {
      const msg =
        error?.data?.error ||
        error?.data?.details ||
        "Failed to update notes. Please try again.";
      toast.error(msg);
    }
  };

  document.title = "Purchase Order Details";

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="alert alert-danger">
            Error loading purchase order details.
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Purchase Order Details" pageTitle="Inventory" />

        <Tooltip
          style={{ zIndex: 10 }}
          anchorSelect=".edit-task-class"
          place="bottom"
          content="Edit PO"
        />
        <Tooltip
          style={{ zIndex: 10 }}
          anchorSelect=".revoke-report-class"
          place="bottom"
          content="Revoke PO"
        />
        <Tooltip
          style={{ zIndex: 10 }}
          anchorSelect=".close-report-class"
          place="bottom"
          content="Close PO"
        />
        <Tooltip
          style={{ zIndex: 10 }}
          anchorSelect=".cancel-report-class"
          place="bottom"
          content="Cancel PO"
        />
        <Tooltip
          style={{ zIndex: 10 }}
          anchorSelect=".create-delivery-class"
          place="bottom"
          content="Create Delivery"
        />

        <Row>
          <Col lg={12}>
            <Card className="mt-n4 mx-n4">
              <div>
                <CardBody className="pb-0 px-4">
                  <Row className="mb-3">
                    <div className="col-md">
                      <Row className="align-items-center g-3">
                        <div className="d-flex justify-content-between">
                          <div>
                            <h4 className="fw-bold">
                              Purchase Order Information
                            </h4>
                          </div>

                          <div>
                            {isPageLoading ? (
                              <>
                                <Spinner size="sm" className="me-2" /> Loading
                                Purchase Order Information...
                              </>
                            ) : (
                              <>
                                {isPOApproved() &&
                                  !isPOCancelled &&
                                  !purchaseOrder?.is_delivery_created && (
                                    <>
                                      {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                                        <Button
                                          color="primary"
                                          size="sm"
                                          className="add-btn create-delivery-class me-1"
                                          onClick={toggleConfirmCreateDeliveryModal}
                                        >
                                          <i className="ri-truck-line align-bottom me-1"></i>
                                          Create Delivery
                                        </Button>
                                      }
                                    </>
                                  )}

                                {/* {isPOApproved() &&
                                  allowRevokePermit(
                                    workFlowData,
                                    selectedRoleName,
                                    getEmployeeUUID
                                  ) && (
                                    <Button
                                      color="primary"
                                      size="sm"
                                      className="add-btn revoke-report-class me-1"
                                      onClick={toggleConfirmRevokeModal}
                                    >
                                      <i className="ri-door-lock-box-line align-bottom me-1"></i>
                                      Revoke PO
                                    </Button>
                                  )} */}

                                {/* {isPOApproved() &&
                                  allowClosePermit(
                                    workFlowData,
                                    selectedRoleName,
                                    getEmployeeUUID
                                  ) && (
                                    <Button
                                      color="primary"
                                      size="sm"
                                      className="add-btn close-report-class me-1"
                                      onClick={toggleConfirmCloseModal}
                                    >
                                      <i className="ri-door-lock-box-line align-bottom me-1"></i>
                                      Close PO
                                    </Button>
                                  )} */}

                                {(isPODraft() ||
                                  purchaseOrder?.approval_status_name ===
                                  "resubmission") && (
                                    <Button
                                      color="primary"
                                      size="sm"
                                      className="add-btn me-1 edit-task-class"
                                      onClick={() => {
                                        navigate(
                                          `/purchase-orders/edit/${purchaseOrder?.id}`
                                        );
                                      }}
                                    >
                                      <i className="ri-pencil-fill align-bottom me-1"></i>
                                      Edit PO
                                    </Button>
                                  )}

                                {canEditNotes() && (
                                  <Button
                                    color="warning"
                                    size="sm"
                                    className="add-btn me-1"
                                    onClick={openEditNotesModal}
                                  >
                                    <i className="ri-edit-line align-bottom me-1"></i>
                                    Edit Notes
                                  </Button>
                                )}

                                {(isPODraft() ||
                                  purchaseOrder?.approval_status_name ===
                                  "resubmission") &&
                                  IsValidPermissions(["DELETE_PURCHASE_ORDER"]) && (
                                    <Button
                                      color="warning"
                                      size="sm"
                                      className="add-btn me-1 cancel-report-class"
                                      onClick={toggleConfirmCancelModal}
                                    >
                                      <i className="ri-close-circle-line align-bottom me-1"></i>
                                      Cancel PO
                                    </Button>
                                  )}

                                {isPODraft() && (
                                  <Button
                                    color="success"
                                    size="sm"
                                    className="add-btn submit-po-class"
                                    onClick={() => {
                                      console.log("Submit PO for approval");
                                    }}
                                  >
                                    <i className="ri-send-plane-fill align-bottom me-1"></i>
                                    Submit for Approval
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <hr />
                        <div className="col-md">
                          <div className="d-flex justify-content-between">
                            <div>
                              <h4 className="fw-bold">
                                PO Number :{" "}
                                <span className="text-muted">
                                  ({purchaseOrder?.inventory_purchase_order_number || "N/A"})
                                </span>
                              </h4>
                            </div>

                            <div className="hstack gap-3 flex-wrap">
                              {purchaseOrder?.po_status && (
                                <>
                                  <div className="vr"></div>
                                  <div>
                                    PO Status :{" "}
                                    {getPOStatusBadge(isPOCancelled ? "cancelled" : purchaseOrder.po_status)}
                                  </div>
                                </>
                              )}
                              {purchaseOrder?.approval_status_name && (
                                <>
                                  <div className="vr"></div>
                                  <div>
                                    Approval Status :{" "}
                                    <Badge
                                      color={
                                        purchaseOrder.approval_status_name ===
                                          "approved"
                                          ? "success"
                                          : purchaseOrder.approval_status_name ===
                                            "rejected"
                                            ? "danger"
                                            : purchaseOrder.approval_status_name ===
                                              "pending"
                                              ? "warning"
                                              : "secondary"
                                      }
                                    >
                                      {purchaseOrder.approval_status_name}
                                    </Badge>
                                  </div>
                                </>
                              )}
                              {purchaseOrder?.delivery_status && (
                                <>
                                  <div className="vr"></div>
                                  <div>
                                    Delivery Status :{" "}
                                    {getDeliveryStatusBadge(
                                      purchaseOrder.delivery_status
                                    )}
                                  </div>
                                </>
                              )}
                              <div className="vr"></div>
                              <div className="fw-bold text-success">
                                Total: <PriceViewCompact price={calculateTotalCost().toFixed(2)} />
                              </div>
                              {purchaseOrder?.po_status &&
                                !isPOCancelled &&
                                IsValidPermissions(["DELETE_PURCHASE_ORDER"]) && (
                                  <>
                                    <div className="vr"></div>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      onClick={() => {
                                        setCancelPOError(null);
                                        setCancelPOModalOpen(true);
                                      }}
                                    >
                                      <i className="ri-close-circle-line align-bottom me-1"></i>
                                      Cancel PO
                                    </button>
                                  </>
                                )}
                            </div>
                          </div>
                        </div>
                        {isPOCancelled && (
                          <Alert color="danger" className="mt-2 mb-0 py-2">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div>
                                <i className="ri-close-circle-line me-2"></i>
                                <strong>
                                  This purchase order has been cancelled.
                                </strong>
                                {purchaseOrder?.cancellation_reason && (
                                  <> Reason: {purchaseOrder.cancellation_reason}</>
                                )}
                                {purchaseOrder?.cancelled_by_name && (
                                  <> — by {purchaseOrder.cancelled_by_name}</>
                                )}
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => setShowCancellationEmailModal(true)}
                              >
                                <i className="ri-mail-send-line align-bottom me-1"></i>
                                Send Cancellation Email
                              </button>
                            </div>
                          </Alert>
                        )}
                        {purchaseOrder?.source_undeliverable_po_number && (
                          <Alert color="info" className="mt-2 mb-0 py-2">
                            <i className="ri-link me-2"></i>
                            <strong>Created from undeliverable PO:</strong>{" "}
                            {purchaseOrder.source_undeliverable_po_number}
                            {purchaseOrder.source_undeliverable_po_supplier && (
                              <> (Supplier: {purchaseOrder.source_undeliverable_po_supplier})</>
                            )}
                          </Alert>
                        )}
                      </Row>
                      {(projectBudgetAllocations?.length > 0 || projectAllocatedBudget != null) && (() => {
                        const poTotalCost = calculateTotalCost();
                        const projectLimit =
                          projectAllocatedBudget != null &&
                          String(projectAllocatedBudget).trim() !== ""
                            ? Number(
                                String(projectAllocatedBudget).replace(
                                  /,/g,
                                  "",
                                ),
                              ) || 0
                            : 0;
                        const isPOOverProjectBudget =
                          projectLimit > 0 && poTotalCost > projectLimit;
                        const overBudgetAllocs = (
                          projectBudgetAllocations ?? []
                        ).filter((alloc: any) => {
                          const allocated =
                            Number(alloc.section_allocated_cost ?? 0) || 0;
                          const actual =
                            Number(alloc.actual_cost ?? 0) || 0;
                          return actual > allocated;
                        });
                        const isOverBudget =
                          isPOOverProjectBudget || overBudgetAllocs.length > 0;

                        // Prefer dedicated overbudget_notes field when available.
                        let overBudgetNotesText = "";
                        const rawOver = (purchaseOrder as any)?.overbudget_notes;
                        if (rawOver != null) {
                          const raw = String(rawOver).trim();
                          if (raw && raw.toLowerCase() !== "null" && raw.toLowerCase() !== "undefined") {
                            overBudgetNotesText = raw;
                          }
                        }
                        const hasOverBudgetNotes =
                          overBudgetNotesText.length > 0;

                        return (
                        <Row className="mt-3">
                          <Col xs={12}>
                            <Card className="border shadow-sm">
                              <CardBody className="p-0">
                                <div className="px-3 py-2 border-bottom bg-light d-flex align-items-center justify-content-between">
                                  <div>
                                    <span className="text-muted small text-uppercase fw-semibold">Project budget</span>
                                    {projectAllocatedBudget != null && (
                                      <span className="ms-2 fw-semibold">
                                        Project total limit: <PriceViewCompact price={String(projectAllocatedBudget)} />
                                      </span>
                                    )}
                                  </div>
                                  {projectBudgetAllocations?.length > 0 && (
                                    <Badge color="info">{projectBudgetAllocations.length} section(s)</Badge>
                                  )}
                                </div>

                                {/* Over-budget notes: show when PO is over budget and notes exist */}
                                {isOverBudget && hasOverBudgetNotes && (
                                  <Alert color="danger" className="m-3 mb-2 rounded">
                                    <div className="d-flex align-items-start">
                                      <i className="ri-file-text-line fs-5 me-2 mt-1" />
                                      <div className="flex-grow-1">
                                        <strong className="d-block mb-1">Over-budget notes</strong>
                                        <div className="text-dark">
                                          {overBudgetNotesText}
                                        </div>
                                      </div>
                                    </div>
                                  </Alert>
                                )}
                                
                                {/* Warnings Summary */}
                                {projectBudgetAllocations?.length > 0 && (
                                  <>
                                    {overBudgetAllocs.length > 0 && (
                                      <Alert color="danger" className="m-3 mb-2">
                                        <div className="d-flex align-items-start">
                                          <i className="ri-error-warning-line fs-5 me-2 mt-1" />
                                          <div className="flex-grow-1">
                                            <strong>Budget exceeded:</strong> {overBudgetAllocs.length} section(s) have exceeded their section limit.
                                          </div>
                                        </div>
                                      </Alert>
                                    )}
                                    {(() => {
                                      const nearLimitAllocs = projectBudgetAllocations.filter((alloc: any) => {
                                        const allocated = Number(alloc.section_allocated_cost ?? 0) || 0;
                                        const actual = Number(alloc.actual_cost ?? 0) || 0;
                                        const remaining = Number(alloc.section_remaining_cost ?? 0) || 0;
                                        return allocated > 0 && actual <= allocated && (actual / allocated) >= 0.9 && remaining > 0;
                                      });
                                      return nearLimitAllocs.length > 0 ? (
                                        <Alert color="warning" className="m-3 mb-2">
                                          <div className="d-flex align-items-start">
                                            <i className="ri-alert-line fs-5 me-2 mt-1" />
                                            <div className="flex-grow-1">
                                              <strong>Budget warning:</strong> {nearLimitAllocs.length} section(s) are near their section limit (≥90% utilized).
                                            </div>
                                          </div>
                                        </Alert>
                                      ) : null;
                                    })()}
                                  </>
                                )}

                                {projectBudgetAllocations?.length > 0 && (
                                <div className="table-responsive">
                                  <Table className="table-bordered table-hover mb-0 align-middle">
                                    <thead className="table-light">
                                      <tr>
                                        <th className="text-nowrap">Section</th>
                                        <th className="text-end">Allocated budget</th>
                                        <th className="text-end">This PO (mapped)</th>
                                        <th className="text-end">Purchase cost</th>
                                        <th className="text-end">Remaining balance</th>
                                        <th className="text-center">Utilization</th>
                                        <th className="text-center">Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {projectBudgetAllocations.map((alloc: any) => {
                                        const allocated = Number(alloc.section_allocated_cost ?? 0) || 0;
                                        const remaining = Number(alloc.section_remaining_cost ?? 0) || 0;
                                        const mapped = Number(alloc.mapped_cost ?? 0) || 0;
                                        const actual = Number(alloc.actual_cost ?? 0) || 0;
                                        const utilizationPercent = allocated > 0 ? (actual / allocated) * 100 : 0;
                                        const isOverBudget = actual > allocated;
                                        const isNearLimit = utilizationPercent >= 90 && actual <= allocated;
                                        const willExceedBudget = mapped > remaining;
                                        
                                        return (
                                          <React.Fragment key={alloc.id}>
                                            <tr className={isOverBudget || willExceedBudget ? 'table-danger' : isNearLimit ? 'table-warning' : ''}>
                                              <td className="fw-semibold">
                                                <div className="d-flex align-items-center gap-2">
                                                  <span>{alloc.section_name}</span>
                                                  {isOverBudget && (
                                                    <Badge color="danger" className="d-flex align-items-center gap-1">
                                                      <i className="ri-error-warning-line" />
                                                      Over Budget
                                                    </Badge>
                                                  )}
                                                  {willExceedBudget && !isOverBudget && (
                                                    <Badge color="warning" className="d-flex align-items-center gap-1">
                                                      <i className="ri-alert-line" />
                                                      Will Exceed
                                                    </Badge>
                                                  )}
                                                  {isNearLimit && !isOverBudget && !willExceedBudget && (
                                                    <i className="ri-alert-line text-warning" />
                                                  )}
                                                </div>
                                              </td>
                                              <td className="text-end"><PriceViewCompact price={allocated.toFixed(2)} /></td>
                                              <td className="text-end text-primary fw-semibold"><PriceViewCompact price={mapped.toFixed(2)} /></td>
                                              <td className={`text-end ${isOverBudget ? 'text-danger fw-bold' : ''}`}>
                                                <PriceViewCompact price={actual.toFixed(2)} />
                                              </td>
                                              <td className={`text-end fw-semibold ${remaining > 0 ? 'text-success' : 'text-danger'}`}>
                                                <PriceViewCompact price={remaining.toFixed(2)} />
                                              </td>
                                              <td className="text-center">
                                                <div className="d-flex align-items-center gap-2">
                                                  <Progress
                                                    value={utilizationPercent}
                                                    color={isOverBudget ? "danger" : isNearLimit ? "warning" : "success"}
                                                    className="mb-0 flex-grow-1"
                                                    style={{ height: "6px", minWidth: "60px" }}
                                                  />
                                                  <small className="text-muted">{utilizationPercent.toFixed(0)}%</small>
                                                </div>
                                              </td>
                                              <td className="text-center">
                                                <Badge
                                                  color={alloc.status === "approved" ? "success" : alloc.status === "rejected" ? "danger" : "warning"}
                                                  className="text-capitalize"
                                                >
                                                  {alloc.status}
                                                </Badge>
                                              </td>
                                            </tr>
                                            {willExceedBudget && purchaseOrder?.notes && (
                                              <tr className="table-danger">
                                                <td colSpan={7} className="p-3">
                                                  <Alert color="danger" className="mb-0 py-2">
                                                    <div className="d-flex align-items-start">
                                                      <i className="ri-file-text-line fs-5 me-2 mt-1" />
                                                      <div className="flex-grow-1">
                                                        <strong>Over-Budget Notes:</strong>
                                                        <div className="mt-1">{purchaseOrder.notes}</div>
                                                      </div>
                                                    </div>
                                                  </Alert>
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </tbody>
                                  </Table>
                                </div>
                                )}
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                        );
                      })()}
                    </div>
                  </Row>

                  <Nav
                    className="nav-tabs-custom border-bottom-0"
                    role="tablist"
                    style={{ background: "lavender" }}
                  >
                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "1" },
                          "fw-semibold"
                        )}
                        onClick={() => {
                          toggleTab("1");
                        }}
                        href="#"
                      >
                        Order Items
                      </NavLink>
                    </NavItem>

                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "2" },
                          "fw-semibold",
                          { disabled: isPOCancelled }
                        )}
                        onClick={() => {
                          if (isPOCancelled) return;
                          toggleTab("2");
                        }}
                        style={
                          isPOCancelled
                            ? { pointerEvents: "none", opacity: 0.5 }
                            : undefined
                        }
                        href="#"
                      >
                        Approval
                        {approvalData?.length > 0 && (
                          <Badge color="danger" className="ms-1">
                            {approvalData.length}
                          </Badge>
                        )}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "3" },
                          "fw-semibold"
                        )}
                        onClick={() => {
                          toggleTab("3");
                        }}
                        href="#"
                      >
                        Document Checklist
                      </NavLink>
                    </NavItem>
                    {purchaseOrder?.is_delivery_created &&
                      <NavItem>
                        <NavLink
                          className={classnames(
                            { active: activeTab === "4" },
                            "fw-semibold"
                          )}
                          onClick={() => {
                            toggleTab("4");
                          }}
                          href="#"
                        >
                          Delivery
                          {purchaseOrder?.is_delivery_created && (
                            <Badge color="success" className="ms-1">
                              Active
                            </Badge>
                          )}
                        </NavLink>
                      </NavItem>
                    }

                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "5" },
                          "fw-semibold"
                        )}
                        onClick={() => {
                          toggleTab("5");
                        }}
                        href="#"
                      >
                        Audit Log
                      </NavLink>
                    </NavItem>

                    {/* <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "3" },
                          "fw-semibold"
                        )}
                        onClick={() => {
                          toggleTab("3");
                        }}
                        href="#"
                      >
                        Order Timeline
                      </NavLink>
                    </NavItem> */}
                  </Nav>
                </CardBody>
              </div>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <TabContent activeTab={activeTab} className="text-muted">
              {/* Order Items Tab */}
              <TabPane tabId="1">
                <Row>
                  <Col xl={12} lg={12}>
                    <Card>
                      <CardBody>
                        <h5 className="mt-3">Order Items</h5>
                        <hr />
                        {/* TEMPORARILY COMMENTED - Mark items as undeliverable - can use later
                        {purchaseOrder?.is_delivery_created &&
                          IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                          (purchaseOrder?.items?.some(
                            (i: any) =>
                              i.delivery_status !== "delivered" && !i.item_undeliverable
                          ) ?? false) && (
                          <Alert color="warning" className="mb-3">
                            <div className="d-flex flex-wrap align-items-center gap-3">
                              <div className="flex-grow-1">
                                <Label for="orderItemsUndeliverableNotes" className="fw-medium">
                                  Mark items as undeliverable (skips delivered items):
                                </Label>
                                <Input
                                  type="textarea"
                                  id="orderItemsUndeliverableNotes"
                                  rows={2}
                                  value={orderItemsUndeliverableNotes}
                                  onChange={(e) =>
                                    setOrderItemsUndeliverableNotes(e.target.value)
                                  }
                                  placeholder="Reason for undeliverable..."
                                  className="mt-1"
                                />
                              </div>
                              <div className="d-flex align-items-end gap-2">
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={handleOrderItemsMarkUndeliverable}
                                  disabled={
                                    isMarkingUndeliverable ||
                                    orderItemsMarkUndeliverableIds.length === 0 ||
                                    !orderItemsUndeliverableNotes.trim()
                                  }
                                >
                                  {isMarkingUndeliverable ? (
                                    <>
                                      <Spinner size="sm" className="me-2" />
                                      Marking...
                                    </>
                                  ) : (
                                    <>
                                      <i className="ri-close-circle-line align-bottom me-1"></i>
                                      Mark Selected as Undeliverable
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </Alert>
                        )}
                        */}
                        <Row className="mt-4">
                          <Col md={12}>
                            {purchaseOrder?.is_delivery_created && (
                              <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                                <div className="d-flex align-items-center gap-2">
                                  <Input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={allOrderItemsPlanningChecked}
                                    disabled={
                                      orderItemsPlanningTargets.length === 0 ||
                                      isMarkingOrderItemsPlanned
                                    }
                                    onChange={(e) =>
                                      handleOrderItemsBulkPlanningToggle(
                                        e.target.checked
                                      )
                                    }
                                  />
                                  <span className="text-muted fs-13">Select all</span>
                                  {isMarkingOrderItemsPlanned && <Spinner size="sm" />}
                                </div>
                                <span className="text-muted fs-13">
                                  Checked = Planned | Unchecked = In preparation
                                </span>
                              </div>
                            )}
                            {purchaseOrder?.items &&
                              purchaseOrder.items.length > 0 ? (
                              <div className="table-responsive">
                                <Table className="table table-nowrap align-middle table-borderless mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      {purchaseOrder?.is_delivery_created && (
                                        <th scope="col" className="text-center">
                                          Delivery planning
                                        </th>
                                      )}
                                      <th scope="col" className="ps-4">
                                        Product Details
                                      </th>
                                      <th scope="col" className="text-center">
                                        Quantity
                                      </th>
                                      <th scope="col" className="text-center">
                                        Expected Delivery Date
                                      </th>
                                      <th scope="col" className="text-center">
                                        Delivery Note
                                      </th>
                                      <th scope="col" className="text-center">
                                        Supplier
                                      </th>
                                      <th scope="col" className="text-center pe-4">
                                        Total Cost
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {purchaseOrder?.items?.map(
                                      (item: any, index: number) => (
                                        <React.Fragment key={item.id}>
                                          <PurchaseOrderProductItem
                                            item={item}
                                            showUndeliverableCheckbox={false}
                                            isMarkUndeliverableSelected={false}
                                            onMarkUndeliverableToggle={undefined}
                                            onToggleDeliveryType={
                                              purchaseOrder?.is_delivery_created
                                                ? handleOrderItemsDeliveryTypeToggle
                                                : undefined
                                            }
                                            onUpdateStockTransferStatus={
                                              handleUpdateStockTransferStatus
                                            }
                                            isUpdatingStockTransfer={
                                              isUpdatingStockTransfer
                                            }
                                            onEditQuantity={
                                              !isPOCancelled &&
                                              IsValidPermissions([
                                                "EDIT_PURCHASE_ORDERS",
                                              ])
                                                ? openEditQuantityModal
                                                : undefined
                                            }
                                          />
                                          {index <
                                            purchaseOrder.items.length - 1 && (
                                              <tr>
                                                <td
                                                  colSpan={
                                                    purchaseOrder?.is_delivery_created ? 7 : 6
                                                  }
                                                  className="p-0"
                                                >
                                                  <hr className="my-1 opacity-25" />
                                                </td>
                                              </tr>
                                            )}
                                        </React.Fragment>
                                      )
                                    )}
                                  </tbody>
                                  {/* Total Cost Footer */}
                                  <tfoot className="table-light">
                                    <tr>
                                      <td
                                        colSpan={
                                          purchaseOrder?.is_delivery_created ? 5 : 5
                                        }
                                        className="text-end fw-bold fs-15"
                                      >
                                        Total Order Cost:
                                      </td>
                                      <td className="text-center fw-bold fs-15 text-success">
                                        <PriceViewCompact price={calculateTotalCost().toFixed(2)} />
                                      </td>
                                      {purchaseOrder?.is_delivery_created &&
                                        IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) && (
                                        <td></td>
                                      )}
                                     
                                    </tr>
                                  </tfoot>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-center py-5">
                                <i className="ri-inbox-line display-4 text-muted opacity-50"></i>
                                <h5 className="mt-3 text-muted">
                                  No Items Found
                                </h5>
                                <p className="text-muted mb-4">
                                  There are no items in this purchase order.
                                </p>
                              </div>
                            )}
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              {/* Approvals Tab */}
              <TabPane tabId="2">
                <ResourceApprovalOverviewTab
                  permitNo={purchaseOrder?.inventory_purchase_order_number}
                  onAfterApprovalStatusChange={async () => { await refetchPurchaseOrder(); }}
                  workFlowData={
                    approvalData
                      ? {
                          ...approvalData,
                          project_allocated_budget:
                            approvalData.project_allocated_budget ??
                            approvalData.project?.project_allocated_budget ??
                            projectAllocatedBudget,
                          project_budget_allocations:
                            approvalData.project_budget_allocations ??
                            projectBudgetAllocations,
                          // Use workflow's own overbudget_notes; do NOT override with PO notes
                          overbudget_notes: approvalData.overbudget_notes,
                          project:
                            approvalData.project != null
                              ? {
                                  ...(typeof approvalData.project === "object" ? approvalData.project : {}),
                                  project_allocated_budget:
                                    (approvalData.project as any)?.project_allocated_budget ?? projectAllocatedBudget,
                                }
                              : projectAllocatedBudget != null
                                ? { project_allocated_budget: projectAllocatedBudget }
                                : undefined,
                        }
                      : approvalData
                  }
                  workFlowLoading={workFlowLoading}
                  refetchProject={refetchProject}
                  confirmCloseModal={confirmCloseModal}
                  confirmRevokeModal={confirmRevokeModal}
                  toggleConfirmCloseModal={toggleConfirmCloseModal}
                  toggleConfirmRevokeModal={toggleConfirmRevokeModal}
                  toggleConfirmCancelModal={toggleConfirmCancelModal}
                  confirmCancelModal={confirmCancelModal}
                  isLoadingCancelPermit={isLoading}
                  cancelPermit={() => { }}
                  permitType={"PURCHASE_ORDER"}
                  setIsPageLoading={setIsPageLoading}
                />
              </TabPane>

              {/* Document Checklist Tab */}
              <TabPane tabId="3">
                <Row>
                  <Col xl={12} lg={12}>
                    <Card>
                      <CardBody>
                        <h5>Document Checklist</h5>
                        <hr />
                        {isDocumentChecklistLoading && (
                          <div className="text-center py-4">
                            <Spinner color="primary" />
                          </div>
                        )}
                        {documentChecklistError && !isDocumentChecklistLoading && (
                          <div className="alert alert-danger">
                            Unable to load document checklist.
                            {" "}
                            {(() => {
                              const err: any = documentChecklistError;
                              const msg =
                                err?.data?.error ||
                                err?.data?.detail ||
                                err?.error ||
                                err?.statusText;
                              return msg ? `Details: ${msg}` : "Please try again.";
                            })()}
                          </div>
                        )}
                        {!isDocumentChecklistLoading && !documentChecklistError && (
                          <>
                            {documentChecklistData?.documents_by_type &&
                            documentChecklistData.documents_by_type.length > 0 ? (
                              <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th scope="col">Document</th>
                                      <th scope="col">Products</th>
                                      <th scope="col" className="text-center">
                                        Uploaded / Required
                                      </th>
                                      <th scope="col" className="text-center">
                                        Status
                                      </th>
                                      <th scope="col" className="text-center">
                                        Uploaded Files
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {documentChecklistData.documents_by_type.map(
                                      (doc: any) => {
                                        const totalRequired =
                                          (doc.product_ids?.length as number) || 0;
                                        const uploadedCount =
                                          (doc.uploaded_count as number) || 0;
                                        const isCompleted =
                                          totalRequired > 0 &&
                                          uploadedCount >= totalRequired;

                                        return (
                                          <tr key={doc.checklist_id}>
                                            <td>
                                              <div className="fw-semibold">
                                                {doc.document_name || "N/A"}
                                              </div>
                                              {doc.description && (
                                                <small className="text-muted d-block">
                                                  {doc.description}
                                                </small>
                                              )}
                                            </td>
                                            <td>
                                              {doc.product_names &&
                                              doc.product_names.length > 0 ? (
                                                <small className="text-muted">
                                                  {doc.product_names.join(", ")}
                                                </small>
                                              ) : (
                                                <span className="text-muted">-</span>
                                              )}
                                            </td>
                                            <td className="text-center">
                                              <span className="fw-semibold">
                                                {uploadedCount} / {totalRequired}
                                              </span>
                                            </td>
                                            <td className="text-center">
                                              {isCompleted ? (
                                                <Badge color="success">Completed</Badge>
                                              ) : (
                                                <Badge color="warning">Pending</Badge>
                                              )}
                                            </td>
                                            <td className="text-center">
                                              {doc.uploaded_documents &&
                                              doc.uploaded_documents.length > 0 ? (
                                                (() => {
                                                  const file = doc.uploaded_documents?.[0];
                                                  return (
                                                    <div className="d-inline-flex align-items-center gap-2 justify-content-center">
                                                      <Button
                                                        color="primary"
                                                        size="sm"
                                                        className="btn-icon d-inline-flex align-items-center justify-content-center rounded-circle"
                                                        tag="a"
                                                        href={file?.attachment_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Preview"
                                                      >
                                                        <i className="ri-eye-line"></i>
                                                      </Button>
                                                      <Button
                                                        color="success"
                                                        size="sm"
                                                        className="btn-icon d-inline-flex align-items-center justify-content-center rounded-circle"
                                                        tag="a"
                                                        href={file?.attachment_url}
                                                        rel="noopener noreferrer"
                                                        download
                                                        title="Download"
                                                      >
                                                        <i className="ri-download-line"></i>
                                                      </Button>
                                                    </div>
                                                  );
                                                })()
                                              ) : (
                                                <span className="text-muted">
                                                  No uploads
                                                </span>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      }
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-center py-5">
                                <i className="ri-file-list-3-line display-4 text-muted opacity-50"></i>
                                <h5 className="mt-3 text-muted">
                                  No checklist configured
                                </h5>
                                <p className="text-muted mb-0">
                                  There are no document checklist items defined for
                                  the products in this purchase order.
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              {/* Delivery Tab */}
              <TabPane tabId="4">
                {purchaseOrder?.is_delivery_created ? (
                  <>
                    {/* PO Cut-off: Undeliverable Items Section */}
                    {undeliverableItems.length > 0 && (
                      <Row className="mb-3">
                        <Col lg={12}>
                          <Card className="border-danger">
                            <CardBody>
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                  <h5 className="mb-1">
                                    <Badge color="danger" className="me-2">
                                      <i className="ri-close-circle-line align-middle me-1"></i>
                                      {undeliverableItems.length}
                                    </Badge>
                                    Undeliverable Items
                                  </h5>
                                  <p className="text-muted mb-0 fs-13">
                                    Items marked as undeliverable. Create a new PO with a different supplier.
                                  </p>
                                  {purchaseOrder?.undeliverable_summary && (
                                    <p className="text-muted mb-0 fs-12 mt-1">
                                      Original balance:{" "}
                                      {Number(
                                        purchaseOrder.undeliverable_summary.original_balance || 0
                                      ).toFixed(2)}{" "}
                                      | Allocated:{" "}
                                      {Number(
                                        purchaseOrder.undeliverable_summary.allocated_amount || 0
                                      ).toFixed(2)}{" "}
                                      | Remaining:{" "}
                                      {Number(
                                        purchaseOrder.undeliverable_summary.remaining_balance || 0
                                      ).toFixed(2)}{" "}
                                      | Split POs:{" "}
                                      {purchaseOrder.undeliverable_summary.split_po_count || 0}
                                    </p>
                                  )}
                                </div>
                                {IsValidPermissions(["ADD_PURCHASE_ORDER"]) &&
                                  Number(purchaseOrder?.undeliverable_summary?.remaining_balance ?? 0) > 0 && (
                                  <Button
                                    color="danger"
                                    onClick={() => {
                                      setSelectedItemIds(
                                        undeliverableItems.map((item: any) => item.id)
                                      );
                                      const remaining =
                                        purchaseOrder?.undeliverable_summary
                                          ?.remaining_balance ?? 0;
                                      setSplitAmount(
                                        Number(remaining).toFixed(2),
                                      );
                                      setShowCreatePOFromUndeliverableModal(true);
                                    }}
                                  >
                                    <i className="ri-add-circle-line align-bottom me-1"></i>
                                    Create New PO from Undeliverable Items
                                  </Button>
                                )}
                              </div>
                              <div className="table-responsive">
                                <Table className="table-sm table-bordered mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th>Product</th>
                                      <th className="text-center">Ordered Qty</th>
                                      <th className="text-center">Delivered Qty</th>
                                      <th className="text-center">Balance Qty</th>
                                      <th className="text-end">Balance Cost</th>
                                      <th>Reason</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {undeliverableItems.map((item: any) => {
                                      const { balanceQty } =
                                        computeUndeliverableBalance(item);
                                      // Use item-level tracked remaining amount so it matches PO summary
                                      const itemRemaining = item.undeliverable_remaining_amount;
                                      const balanceCost =
                                        itemRemaining != null && itemRemaining !== ""
                                          ? Number(itemRemaining)
                                          : (() => {
                                              const { balanceCost: computed } =
                                                computeUndeliverableBalance(item);
                                              const used = Number(item.undeliverable_used_amount) || 0;
                                              return Math.max(0, computed - used);
                                            })();

                                      return (
                                      <tr key={item.id}>
                                        <td>
                                          <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0 avatar-sm bg-light rounded p-1">
                                              <ProductThumbnail
                                                attachment_urls={item?.attachment_urls}
                                                alt={item?.product_name || ''}
                                                size="sm"
                                              />
                                            </div>
                                            <div className="flex-grow-1 ms-2">
                                              <h6 className="fs-13 mb-0">{item.product_name}</h6>
                                              <small className="text-muted">ID: {item.id}</small>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="text-center">{item.quantity || 0}</td>
                                        <td className="text-center">{item.delivered_quantity || 0}</td>
                                        <td className="text-center">
                                          <strong>{balanceQty.toFixed(2)}</strong>
                                        </td>
                                        <td className="text-end">
                                          <strong className="text-success">
                                            {Number(balanceCost).toFixed(2)}
                                          </strong>
                                        </td>
                                        <td>
                                          <small className="text-muted">
                                            {item.undeliverable_notes || "—"}
                                          </small>
                                        </td>
                                      </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                    )}
                    <DeliveryComponent
                      purchaseOrder={purchaseOrder}
                      refetchPurchaseOrder={refetchPurchaseOrder}
                    />
                  </>
                ) : (
                  <Row>
                    <Col xl={12} lg={12}>
                      <Card>
                        <CardBody className="text-center py-5">
                          <i className="ri-truck-line display-4 text-muted opacity-50"></i>
                          <h5 className="mt-3 text-muted">Delivery Not Created</h5>
                          <p className="text-muted mb-4">
                            {isPOCancelled
                              ? "This purchase order has been cancelled."
                              : "Create delivery to start managing item deliveries and defects."}
                          </p>
                          {!isPOCancelled && (
                          <Button
                            color="primary"
                            className="create-delivery-class"
                            onClick={toggleConfirmCreateDeliveryModal}
                          >
                            <i className="ri-truck-line align-bottom me-1"></i>
                            Create Delivery
                          </Button>
                          )}
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                )}
              </TabPane>

              {/* Audit Log Tab */}
              <TabPane tabId="5">
                <Row>
                  <Col xl={12} lg={12}>
                    <AuditLogTab purchaseOrderId={purchaseOrder?.id} />
                  </Col>
                </Row>
              </TabPane>
            </TabContent>
          </Col>
        </Row>

        {/* Create Delivery Confirmation Modal */}
        <Modal isOpen={confirmCreateDeliveryModal} toggle={toggleConfirmCreateDeliveryModal}>
          <ModalHeader toggle={toggleConfirmCreateDeliveryModal}>
            Confirm Create Delivery
          </ModalHeader>
          <ModalBody>
            <p>Are you sure you want to create delivery for this purchase order?</p>
            <p className="text-muted fs-13">
              This will enable delivery management for all items in this purchase order.
              You can then mark items as delivered or raise defects for each item.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleConfirmCreateDeliveryModal}>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleCreateDelivery}
              disabled={isCreatingDelivery}
            >
              {isCreatingDelivery ? "Creating..." : "Create Delivery"}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Create PO from Undeliverable Items Modal */}
        <Modal
          isOpen={showCreatePOFromUndeliverableModal}
          toggle={() => {
            if (!isCreatingPOFromUndeliverable && !isSplittingUndeliverable) {
              setShowCreatePOFromUndeliverableModal(false);
              setSelectedSupplierId(null);
              setSelectedItemIds([]);
              setNewPONotes("");
              setSplitAmount("");
              setSplitGroups([]);
              setActiveGroupIndex(null);
            }
          }}
          size="lg"
        >
          <ModalHeader
            toggle={() => {
              if (!isCreatingPOFromUndeliverable && !isSplittingUndeliverable) {
                setShowCreatePOFromUndeliverableModal(false);
                setSelectedSupplierId(null);
                setSelectedItemIds([]);
                setNewPONotes("");
                setSplitAmount("");
                setSplitGroups([]);
                setActiveGroupIndex(null);
              }
            }}
          >
            <i className="ri-add-circle-line me-2"></i>
            Create New PO from Undeliverable Items
          </ModalHeader>
          <ModalBody>
            <Alert color="info" className="mb-3">
              <i className="ri-information-line me-2"></i>
              Build one or more supplier splits. For each undeliverable item you can allocate
              an amount across suppliers, but the total allocated cannot exceed the item’s
              remaining undeliverable amount.
            </Alert>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <strong>Supplier Splits</strong>
              <Button
                color="primary"
                size="sm"
                onClick={() => {
                  setSplitGroups((prev) => [
                    ...prev,
                    { supplier: null, notes: "", itemAmounts: {} },
                  ]);
                  setActiveGroupIndex(splitGroups.length);
                }}
                disabled={isCreatingPOFromUndeliverable || isSplittingUndeliverable}
              >
                Add Supplier Split
              </Button>
            </div>

            {splitGroups.length === 0 ? (
              <div className="text-muted mb-3">No supplier splits added yet.</div>
            ) : (
              <div className="border rounded p-2 mb-3">
                {splitGroups.map((g, idx) => (
                  <div
                    key={idx}
                    className={`d-flex align-items-center justify-content-between p-2 rounded ${
                      activeGroupIndex === idx ? "bg-soft-primary" : ""
                    }`}
                  >
                    <div>
                      <strong>Split #{idx + 1}</strong>{" "}
                      <span className="text-muted">
                        {g.supplier?.supplier_name ||
                          g.supplier?.name ||
                          g.supplier?.stock_reference_number ||
                          (g.supplier?.source_mode === "stock"
                            ? "Stock source selected"
                            : "No supplier")}
                      </span>
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        color="outline-primary"
                        size="sm"
                        onClick={() => setActiveGroupIndex(idx)}
                        disabled={isCreatingPOFromUndeliverable || isSplittingUndeliverable}
                      >
                        Edit
                      </Button>
                      <Button
                        color="outline-danger"
                        size="sm"
                        onClick={() => {
                          setSplitGroups((prev) => prev.filter((_, i) => i !== idx));
                          setActiveGroupIndex((prev) =>
                            prev === idx ? null : prev != null && prev > idx ? prev - 1 : prev
                          );
                        }}
                        disabled={isCreatingPOFromUndeliverable || isSplittingUndeliverable}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeGroupIndex != null && splitGroups[activeGroupIndex] && (
              <div className="border rounded p-3 mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <strong>Edit Split #{activeGroupIndex + 1}</strong>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => setIsSupplierModalOpen(true)}
                    disabled={isCreatingPOFromUndeliverable || isSplittingUndeliverable}
                  >
                    Choose Supplier
                  </Button>
                </div>

                <div className="mb-2 text-muted">
                  Supplier:{" "}
                  <strong>
                    {splitGroups[activeGroupIndex].supplier?.supplier_name ||
                      splitGroups[activeGroupIndex].supplier?.name ||
                      splitGroups[activeGroupIndex].supplier?.stock_reference_number ||
                      (splitGroups[activeGroupIndex].supplier?.source_mode === "stock"
                        ? "Stock source selected"
                        : "") ||
                      "No supplier selected"}
                  </strong>
                </div>

                <FormGroup>
                  <Label>Notes (optional)</Label>
                  <Input
                    type="text"
                    value={splitGroups[activeGroupIndex].notes}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSplitGroups((prev) =>
                        prev.map((p, i) => (i === activeGroupIndex ? { ...p, notes: v } : p)),
                      );
                    }}
                    disabled={isCreatingPOFromUndeliverable || isSplittingUndeliverable}
                  />
                </FormGroup>

                <div className="mt-2">
                  <strong>Allocate amounts</strong>
                  <div className="text-muted fs-12">
                    Per item max is its remaining after other splits in this form.
                  </div>
                </div>

                <div className="mt-2 border rounded">
                  {undeliverableItems.map((item: any) => {
                    const itemTotalRemaining =
                      parseFloat(item.undeliverable_remaining_amount) ||
                      (computeUndeliverableBalance(item).balanceCost -
                        (parseFloat(item.undeliverable_used_amount) || 0));
                    // Subtract amounts already allocated in other split groups (same modal)
                    const allocatedInOtherGroups = splitGroups.reduce(
                      (sum, g, idx) =>
                        idx === activeGroupIndex
                          ? sum
                          : sum + (parseFloat(g.itemAmounts[item.id] || "0") || 0),
                      0
                    );
                    const remaining = Math.max(
                      0,
                      itemTotalRemaining - allocatedInOtherGroups
                    );
                    const current =
                      splitGroups[activeGroupIndex].itemAmounts[item.id] || "";
                    const isZeroRemaining = remaining <= 0;
                    return (
                      <div
                        key={item.id}
                        className="d-flex align-items-center justify-content-between p-2 border-bottom"
                      >
                        <div>
                          <strong>{item.product_name}</strong>
                          <div className="text-muted fs-12">
                            Remaining: {remaining.toFixed(2)}
                            {allocatedInOtherGroups > 0 && (
                              <span className="ms-1">
                                (total {itemTotalRemaining.toFixed(2)} − {allocatedInOtherGroups.toFixed(2)} in other splits)
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Input
                            type="number"
                            min={0}
                            step={0.01}
                            max={remaining}
                            value={current}
                            placeholder="0.00"
                            onChange={(e) => {
                              if (isZeroRemaining) return;
                              const val = e.target.value;
                              const num = parseFloat(val);
                              const toStore =
                                val === "" || !Number.isFinite(num)
                                  ? val
                                  : remaining > 0
                                    ? String(Math.min(Math.max(0, num), remaining))
                                    : val;
                              setSplitGroups((prev) =>
                                prev.map((p, i) =>
                                  i === activeGroupIndex
                                    ? {
                                        ...p,
                                        itemAmounts: { ...p.itemAmounts, [item.id]: toStore },
                                      }
                                    : p,
                                ),
                              );
                            }}
                            onBlur={(e) => {
                              if (isZeroRemaining) return;
                              const val = e.target.value;
                              const num = parseFloat(val);
                              if (val !== "" && Number.isFinite(num) && remaining > 0) {
                                const capped = Math.min(Math.max(0, num), remaining);
                                if (capped !== num) {
                                  setSplitGroups((prev) =>
                                    prev.map((p, i) =>
                                      i === activeGroupIndex
                                        ? { ...p, itemAmounts: { ...p.itemAmounts, [item.id]: String(capped) } }
                                        : p,
                                    ),
                                  );
                                }
                              }
                            }}
                            disabled={
                              isCreatingPOFromUndeliverable ||
                              isSplittingUndeliverable ||
                              isZeroRemaining
                            }
                            style={{ width: 140 }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => {
                setShowCreatePOFromUndeliverableModal(false);
                setSelectedSupplierId(null);
                setSelectedItemIds([]);
                setNewPONotes("");
                setSplitAmount("");
                setSplitGroups([]);
                setActiveGroupIndex(null);
              }}
              disabled={isCreatingPOFromUndeliverable || isSplittingUndeliverable}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={async () => {
                try {
                  const validGroups = splitGroups
                    .map((g) => ({
                      supplier_id: g.supplier?.supplier_id ?? g.supplier?.id,
                      notes: g.notes || undefined,
                      items: Object.entries(g.itemAmounts)
                        .map(([k, v]) => ({
                          parent_item_id: Number(k),
                          amount: parseFloat(String(v || "0")),
                        }))
                        .filter((r) => r.amount > 0),
                    }))
                    .filter((g) => g.supplier_id && g.items.length > 0);

                  if (validGroups.length === 0) {
                    toast.error("Add at least one supplier split with amounts.");
                    return;
                  }

                  const perItemTotal: Record<number, number> = {};
                  validGroups.forEach((g) => {
                    g.items.forEach((it) => {
                      perItemTotal[it.parent_item_id] =
                        (perItemTotal[it.parent_item_id] || 0) + it.amount;
                    });
                  });

                  for (const item of undeliverableItems) {
                    const remaining =
                      parseFloat(item.undeliverable_remaining_amount) ||
                      (computeUndeliverableBalance(item).balanceCost -
                        (parseFloat(item.undeliverable_used_amount) || 0));
                    const used = perItemTotal[item.id] || 0;
                    if (used - 1e-6 > Math.max(0, remaining)) {
                      toast.error(
                        `Allocated amount for "${item.product_name}" exceeds remaining balance.`,
                      );
                      return;
                    }
                  }

                  const res = await splitUndeliverable({
                    poId: purchaseOrder?.id,
                    body: { splits: validGroups as any },
                  }).unwrap();

                  toast.success(
                    `Created ${res?.created_po_count || 0} split PO(s) successfully.`,
                  );
                  setShowCreatePOFromUndeliverableModal(false);
                  setSplitGroups([]);
                  setActiveGroupIndex(null);
                  setSelectedSupplierId(null);
                  setSelectedSupplierDetails(null);
                  refetchPurchaseOrder();
                } catch (e: any) {
                  toast.error(e?.data?.error || "Failed to split undeliverable PO.");
                }
              }}
              disabled={
                isCreatingPOFromUndeliverable ||
                isSplittingUndeliverable ||
                splitGroups.length === 0
              }
            >
              {isCreatingPOFromUndeliverable || isSplittingUndeliverable ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                <>
                  <i className="ri-add-circle-line align-bottom me-1"></i>
                  Create Split PO(s)
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Reusable Supplier Selection Modal for undeliverable split */}
        <SupplierSelectionModal
          isOpen={isSupplierModalOpen}
          toggle={() => setIsSupplierModalOpen(false)}
          productId={
            activeSplitItemForSupplierModal?.product ||
            undeliverableItems[0]?.product ||
            null
          }
          productName={
            activeSplitItemForSupplierModal?.product_name ||
            undeliverableItems[0]?.product_name
          }
          targetProjectId={
            purchaseOrder?.project_id ?? purchaseOrder?.project ?? null
          }
          requestedQty={
            activeSplitItemForSupplierModal?.quantity != null
              ? Number(activeSplitItemForSupplierModal.quantity)
              : null
          }
          currentSupplierId={
            activeGroupIndex != null && splitGroups[activeGroupIndex]?.supplier
              ? splitGroups[activeGroupIndex].supplier.supplier_id ?? splitGroups[activeGroupIndex].supplier.id
              : selectedSupplierId
          }
          onSelect={(selection: any) => {
            setSelectedSupplierDetails(selection);
            const nextSupplierId =
              selection?.source_mode === "stock"
                ? null
                : selection?.supplier_id || selection?.id || null;
            setSelectedSupplierId(nextSupplierId);
            if (activeGroupIndex != null && splitGroups[activeGroupIndex]) {
              setSplitGroups((prev) =>
                prev.map((p, i) =>
                  i === activeGroupIndex ? { ...p, supplier: selection } : p,
                ),
              );
            }
            setIsSupplierModalOpen(false);
          }}
        />
      </Container>

      {/* Cancel PO Modal */}
      <CancelReasonModal
        show={cancelPOModalOpen}
        title="Cancel Purchase Order"
        description="This will cancel the purchase order and return its mapped budget to the project. You can then send the supplier a cancellation email manually (with the cancelled PDF attached)."
        isSubmitting={isCancellingPO}
        errorText={cancelPOError}
        errorItems={cancelPOErrorItems}
        onConfirm={handleCancelPO}
        onClose={() => setCancelPOModalOpen(false)}
      />

      {/* Manual cancellation email — reuses the PO email modal; the backend attaches the cancelled PDF */}
      {purchaseOrder?.id && (
        <POEmailModal
          isOpen={showCancellationEmailModal}
          toggle={() => setShowCancellationEmailModal(false)}
          poId={purchaseOrder.id}
          poNumber={purchaseOrder.inventory_purchase_order_number}
        />
      )}

      {/* PO Cancellation Result — shows budget returned to project (permission-gated) */}
      <Modal isOpen={!!cancelPOResult} toggle={() => setCancelPOResult(null)} centered>
        <ModalHeader toggle={() => setCancelPOResult(null)}>
          Purchase Order Cancelled
        </ModalHeader>
        <ModalBody>
          <div className="text-center mb-3">
            <i className="ri-checkbox-circle-line text-success" style={{ fontSize: "3rem" }}></i>
            <h5 className="mt-2">Cancellation Successful</h5>
            <p className="text-muted mb-0">
              This purchase order has been cancelled
              {cancelPOResult?.email_sent ? " and the supplier has been notified." : "."}
            </p>
          </div>

          <div className="border rounded p-3 bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <span className="fw-semibold">Budget returned to project</span>
              {IsValidPermissions(["VIEW_ALL_PURCHASE_COST"]) ? (
                <span className="fw-bold text-success">
                  <PriceViewCompact price={Number(cancelPOResult?.returned_budget_total ?? 0).toFixed(2)} />
                </span>
              ) : (
                <span className="text-muted fst-italic">
                  <i className="ri-lock-line me-1"></i>Restricted
                </span>
              )}
            </div>

            {IsValidPermissions(["VIEW_ALL_PURCHASE_COST"]) &&
              Array.isArray(cancelPOResult?.returned_budget_sections) &&
              cancelPOResult.returned_budget_sections.length > 0 && (
                <ul className="mt-2 mb-0 ps-3 small text-muted">
                  {cancelPOResult.returned_budget_sections.map((s: any, idx: number) => (
                    <li key={idx}>
                      {s.section_name}: {Number(s.amount).toFixed(2)}
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => setCancelPOResult(null)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* PO Update flow: Edit Item Quantity Modal */}
      <Modal isOpen={editQtyModalOpen} toggle={closeEditQuantityModal} centered>
        <ModalHeader toggle={closeEditQuantityModal}>Edit Item Quantity</ModalHeader>
        <ModalBody>
          {editQtyItem && (
            <>
              <div className="mb-3">
                <div className="fw-semibold">{editQtyItem.product_name}</div>
                <small className="text-muted">
                  Current quantity:{" "}
                  {editQtyItem?.quantity ?? editQtyItem?.ordered_quantity ?? 0}
                  {editQtyItem?.measurement_name
                    ? ` ${editQtyItem.measurement_name}`
                    : ""}
                </small>
              </div>

              <FormGroup>
                <Label className="fw-semibold">
                  New Quantity <span className="text-danger">*</span>
                </Label>
                <Input
                  type="number"
                  min={0}
                  step="any"
                  value={editQtyValue}
                  onChange={(e) => setEditQtyValue(e.target.value)}
                />
              </FormGroup>

              {(() => {
                const currentQty = Number(
                  editQtyItem?.quantity ?? editQtyItem?.ordered_quantity ?? 0
                );
                const parsed = Number(editQtyValue);
                const validNew = !Number.isNaN(parsed) && parsed >= 0;
                const deltaQty = validNew ? parsed - currentQty : 0;
                const unitPrice =
                  Number(editQtyItem?.effective_unit_price) ||
                  (currentQty
                    ? Number(
                        editQtyItem?.effective_total_item_cost ??
                          editQtyItem?.total_item_cost ??
                          0
                      ) / currentQty
                    : 0);
                const budgetImpact = deltaQty * unitPrice;
                const isIncrease = deltaQty > 0;
                const showReapproval =
                  purchaseOrder?.po_status === "approved" && isIncrease;
                const showDeliveryGrouping =
                  isIncrease && !!purchaseOrder?.is_delivery_created;

                return (
                  <>
                    {validNew && deltaQty !== 0 && (
                      <div className="border rounded p-2 mb-3 bg-light small">
                        <div className="d-flex justify-content-between">
                          <span>Quantity change</span>
                          <span
                            className={
                              isIncrease ? "text-danger" : "text-success"
                            }
                          >
                            {isIncrease ? "+" : ""}
                            {deltaQty}
                          </span>
                        </div>
                        {IsValidPermissions(["VIEW_ALL_PURCHASE_COST"]) && (
                          <div className="d-flex justify-content-between">
                            <span>
                              Estimated budget{" "}
                              {isIncrease ? "consumed" : "returned"}
                            </span>
                            <span
                              className={
                                isIncrease ? "text-danger" : "text-success"
                              }
                            >
                              {Math.abs(budgetImpact).toFixed(2)}
                            </span>
                          </div>
                        )}
                        <div className="text-muted mt-1">
                          {isIncrease
                            ? "Increasing quantity will take the additional amount from the project budget."
                            : "Reducing quantity will return the difference to the project budget."}
                        </div>
                      </div>
                    )}

                    {showDeliveryGrouping && (
                      <FormGroup className="mb-3">
                        <Label className="fw-semibold d-block">
                          Delivery for the added {deltaQty} unit(s)
                        </Label>
                        <FormGroup check className="mb-1">
                          <Input
                            type="radio"
                            id="editQtyDeliveryMerge"
                            name="editQtyDeliveryAction"
                            checked={editQtyDeliveryAction === "merge"}
                            onChange={() => setEditQtyDeliveryAction("merge")}
                          />
                          <Label
                            check
                            for="editQtyDeliveryMerge"
                            className="ms-1"
                          >
                            Add to the existing delivery (same date)
                          </Label>
                        </FormGroup>
                        <FormGroup check className="mb-1">
                          <Input
                            type="radio"
                            id="editQtyDeliveryNew"
                            name="editQtyDeliveryAction"
                            checked={editQtyDeliveryAction === "new_record"}
                            onChange={() =>
                              setEditQtyDeliveryAction("new_record")
                            }
                          />
                          <Label check for="editQtyDeliveryNew" className="ms-1">
                            Create a new delivery record (choose a date)
                          </Label>
                        </FormGroup>
                        {editQtyDeliveryAction === "new_record" && (
                          <Input
                            type="date"
                            className="mt-1"
                            value={editQtyDeliveryDate}
                            onChange={(e) =>
                              setEditQtyDeliveryDate(e.target.value)
                            }
                          />
                        )}
                      </FormGroup>
                    )}

                    {showReapproval && (
                      <FormGroup check className="mb-3">
                        <Input
                          type="checkbox"
                          id="editQtyNeedsReapproval"
                          checked={editQtyNeedsReapproval}
                          onChange={(e) =>
                            setEditQtyNeedsReapproval(e.target.checked)
                          }
                        />
                        <Label
                          check
                          for="editQtyNeedsReapproval"
                          className="ms-1"
                        >
                          Requires re-approval for this increase
                        </Label>
                        <div className="text-muted fs-12 mt-1">
                          When checked, a new approval is started for only the
                          added quantity/amount. The original approval is kept as
                          history, and the extra budget is consumed only once this
                          delta approval is approved.
                        </div>
                      </FormGroup>
                    )}
                  </>
                );
              })()}

              <FormGroup>
                <Label className="fw-semibold">Reason / Notes</Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={editQtyReason}
                  onChange={(e) => setEditQtyReason(e.target.value)}
                  placeholder="Optional: why is this quantity being changed?"
                />
              </FormGroup>

              {editQtyError && (
                <div className="alert alert-danger py-2 mb-0">{editQtyError}</div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={closeEditQuantityModal}
            disabled={isUpdatingQuantity}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmitEditQuantity}
            disabled={isUpdatingQuantity}
          >
            {isUpdatingQuantity ? (
              <>
                <Spinner size="sm" className="me-1" /> Updating...
              </>
            ) : (
              "Update Quantity"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Notes Modal */}
      <Modal isOpen={editNotesModal.open} toggle={closeEditNotesModal}>
        <ModalHeader toggle={closeEditNotesModal}>Edit PO Notes</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className="fw-semibold d-flex justify-content-between align-items-center">
              <span>
                Notes / Instructions <span className="text-danger">*</span>
              </span>
              <Button
                size="sm"
                color="outline-primary"
                type="button"
                onClick={() =>
                  setEditNotesModal((prev) => ({
                    ...prev,
                    notes: [...prev.notes, ""],
                  }))
                }
              >
                <i className="ri-add-line me-1" />
                Add Note
              </Button>
            </Label>

            {editNotesModal.notes.map((note, idx) => {
              const hasError = editNotesModal.notes.every((n) => !n.trim());
              return (
                <div className="d-flex align-items-start mt-2" key={idx}>
                  <Input
                    type="textarea"
                    rows={2}
                    placeholder={`Note ${idx + 1}`}
                    value={note}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditNotesModal((prev) => {
                        const copy = [...prev.notes];
                        copy[idx] = value;
                        return { ...prev, notes: copy };
                      });
                    }}
                    invalid={hasError}
                  />
                  {editNotesModal.notes.length > 1 && (
                    <Button
                      size="sm"
                      color="link"
                      type="button"
                      className="ms-2 text-danger"
                      onClick={() =>
                        setEditNotesModal((prev) => ({
                          ...prev,
                          notes: prev.notes.filter((_, i) => i !== idx),
                        }))
                      }
                    >
                      <i className="ri-close-circle-line" />
                    </Button>
                  )}
                </div>
              );
            })}
          </FormGroup>
          <Alert color="info" className="mt-2 mb-0">
            Notes can be edited only when PO status is <strong>submitted</strong> and
            approval status is <strong>pending</strong>.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeEditNotesModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveNotes}>
            Save Notes
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default InventoryPurchaseOrderDetails;
